/**
 * Camera Offset Trigger Behavior
 * 
 * Changes the follow camera's offset when the player enters the trigger zone.
 * Uses a Camera node placed in the scene as a reference point.
 * 
 * The offset is calculated as: cameraPosition - triggerPosition (world space),
 * then converted to tangent space for proper follow-player camera behavior.
 * 
 * Features:
 * - Smooth camera transition on enter
 * - Restores previous camera offset on exit
 * - Configurable transition duration
 * - Optional camera rotation preservation (future enhancement)
 */

import { Vector3, type Curve3, type Camera } from '@babylonjs/core';
import { EntityType } from '../../types/EntityTypes';
import type { TriggerBehavior } from '../../types/TriggerTypes';
import { logger } from '../../core/Logger';
import { computeTangentFrame, worldToTangent } from '../../systems/camera/TangentSpace';
import type { FollowPlayerData } from '../../systems/CameraController';

interface StoredCameraState {
  previousOffset: Vector3;
  previousLookAtOffset: Vector3;
  previousSmoothing: number;
}

/**
 * Get camera world position accounting for different camera types
 */
function getCameraWorldPosition(cam: Camera): Vector3 {
  try {
    cam.computeWorldMatrix();
  } catch {
    // ignore
  }
  const anyCam = cam as any;
  if (typeof anyCam.getAbsolutePosition === 'function') return anyCam.getAbsolutePosition();
  if (anyCam.globalPosition) return anyCam.globalPosition as Vector3;
  return cam.position;
}

/**
 * Find t-value (0-1) for a position on spline using arc length parameterization
 */
function findTForPosition(spline: Curve3, position: Vector3): number {
  const points = spline.getPoints();
  let closestDistance = Infinity;
  let closestIndex = 0;
  let closestSegmentT = 0;

  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i+1];
    const segment = p2.subtract(p1);
    const lenSq = segment.lengthSquared();
    
    // Project point onto segment
    const pointVec = position.subtract(p1);
    const t = Math.max(0, Math.min(1, Vector3.Dot(pointVec, segment) / lenSq));
    
    const closest = p1.add(segment.scale(t));
    const dist = Vector3.Distance(closest, position);
    
    if (dist < closestDistance) {
      closestDistance = dist;
      closestIndex = i;
      closestSegmentT = t;
    }
  }

  // Calculate accumulated length
  let currentLength = 0;
  for (let i = 0; i < closestIndex; i++) {
    currentLength += Vector3.Distance(points[i], points[i+1]);
  }
  currentLength += Vector3.Distance(points[closestIndex], points[closestIndex+1]) * closestSegmentT;

  return currentLength / spline.length();
}

export const cameraOffsetChangeBehavior: TriggerBehavior = {
  id: 'trigger:camera_offset_change',
  displayName: 'Camera Offset Change',
  type: EntityType.TRIGGER_ZONE,
  category: 'trigger',
  tags: ['camera', 'cinematic'],
  
  parameters: {
    cameraNodeName: {
      type: 'string',
      default: '',
      description: 'Name of the Camera node in the scene to use as offset reference'
    },
    preserveFacing: {
      type: 'boolean',
      default: false,
      description: 'Also apply camera rotation/target (future enhancement - not yet implemented)'
    },
    transitionDuration: {
      type: 'number',
      default: 1.0,
      min: 0.1,
      max: 5.0,
      description: 'Duration of camera transition in seconds'
    },
    smoothing: {
      type: 'number',
      default: 0.1,
      min: 0.01,
      max: 0.5,
      description: 'Camera smoothing factor (lower = smoother)'
    }
  },
  
  onEnter: (api) => {
    const { ctx, services } = api;
    const { params } = ctx;
    
    // Get parameters
    const cameraNodeName = (params.cameraNodeName as string) ?? '';
    const transitionDuration = (params.transitionDuration as number) ?? 1.0;
    const smoothing = (params.smoothing as number) ?? 0.1;
    const preserveFacing = (params.preserveFacing as boolean) ?? false;
    
    if (!cameraNodeName) {
      logger.triggers.error(`❌ camera_offset_change: No cameraNodeName specified for trigger ${ctx.self.id}`);
      return;
    }
    
    // Find camera node
    const camera = services.scene.getCameraByName(cameraNodeName);
    if (!camera) {
      logger.triggers.error(`❌ camera_offset_change: Camera node '${cameraNodeName}' not found in scene`);
      return;
    }
    
    // Get trigger world position - need to account for transform hierarchy
    // The trigger mesh is available via services.scene
    const triggerMesh = services.scene.getMeshByName(ctx.self.id);
    let triggerPos: Vector3;
    
    if (triggerMesh) {
      triggerMesh.computeWorldMatrix(true);
      triggerPos = triggerMesh.getAbsolutePosition();
    } else {
      // Fallback to stored position (may not account for hierarchy)
      logger.triggers.warn(`⚠️ camera_offset_change: Could not find trigger mesh, using stored position`);
      triggerPos = ctx.position;
    }
    
    // Get camera world position (accounting for full transform hierarchy)
    const cameraPos = getCameraWorldPosition(camera);
    
    // Calculate world-space offset: camera position relative to trigger position
    const worldOffset = cameraPos.subtract(triggerPos);
    
    logger.triggers.log(
      `📷 camera_offset_change: Trigger at (${triggerPos.x.toFixed(2)}, ${triggerPos.y.toFixed(2)}, ${triggerPos.z.toFixed(2)}), ` +
      `Camera '${cameraNodeName}' at (${cameraPos.x.toFixed(2)}, ${cameraPos.y.toFixed(2)}, ${cameraPos.z.toFixed(2)}), ` +
      `World offset: (${worldOffset.x.toFixed(2)}, ${worldOffset.y.toFixed(2)}, ${worldOffset.z.toFixed(2)})`
    );
    
    // Get player position - use entrant if it's the player, otherwise get from registry
    const player = ctx.entrant.isPlayer ? ctx.entrant : services.entities.getPlayer();
    if (!player?.mesh) {
      logger.triggers.error(`❌ camera_offset_change: Cannot get player mesh`);
      return;
    }
    
    const playerPos = player.mesh.getAbsolutePosition();
    
    // Get spline from camera controller's current segment
    const currentSegment = services.camera.getCurrentSegment();
    if (!currentSegment) {
      logger.triggers.warn(`⚠️ camera_offset_change: Camera is not in follow-player mode, cannot apply offset change`);
      return;
    }
    
    const currentData = currentSegment.data as FollowPlayerData;
    const spline = currentData.spline;
    
    if (!spline) {
      logger.triggers.error(`❌ camera_offset_change: No spline available in camera segment`);
      return;
    }
    
    // Convert world offset to tangent space
    const tangentFrame = computeTangentFrame(spline, playerPos);
    const tangentOffset = worldToTangent(tangentFrame, worldOffset);
    
    logger.triggers.log(
      `📷 camera_offset_change: Tangent offset: X=${tangentOffset.x.toFixed(2)} (up), ` +
      `Y=${tangentOffset.y.toFixed(2)} (forward), Z=${tangentOffset.z.toFixed(2)} (right)`
    );
    
    // Store previous camera state for restoration on exit
    if (currentSegment) {
      const previousState: StoredCameraState = {
        previousOffset: currentData.offset.clone(),
        previousLookAtOffset: currentData.lookAtOffset?.clone() ?? new Vector3(0, 0, 0),
        previousSmoothing: currentData.smoothing
      };
      
      // Store in trigger metadata
      if (!ctx.self.metadata) {
        (ctx.self as any).metadata = {};
      }
      ctx.self.metadata!.cameraState = previousState;
      
      logger.triggers.log(
        `📷 camera_offset_change: Stored previous offset: (${previousState.previousOffset.x.toFixed(2)}, ` +
        `${previousState.previousOffset.y.toFixed(2)}, ${previousState.previousOffset.z.toFixed(2)})`
      );
    }
    
    // Get current T value for player position on spline
    const currentT = findTForPosition(spline, playerPos);
    
    // Apply new camera offset
    const newCameraData: FollowPlayerData = {
      offset: tangentOffset,
      lookAtOffset: new Vector3(0, 0, 0), // Look at player center
      smoothing: smoothing,
      spline: spline
    };
    
    services.camera.setCameraMode(
      currentSegment.type,
      newCameraData,
      currentT,
      transitionDuration
    );
    
    logger.triggers.log(
      `📷 camera_offset_change: Applied new camera offset with ${transitionDuration}s transition`
    );
    
    // Future enhancement: Apply camera rotation if preserveFacing is true
    if (preserveFacing) {
      logger.triggers.warn(`⚠️ camera_offset_change: preserveFacing is not yet implemented`);
    }
  },
  
  onExit: (api) => {
    const { ctx, services } = api;
    
    // Retrieve stored camera state
    const storedState = ctx.self.metadata?.cameraState as StoredCameraState | undefined;
    if (!storedState) {
      logger.triggers.warn(`⚠️ camera_offset_change: No previous camera state stored, cannot restore`);
      return;
    }
    
    // Get spline from camera controller
    const currentSegment = services.camera.getCurrentSegment();
    if (!currentSegment) {
      logger.triggers.warn(`⚠️ camera_offset_change: Camera is not in follow-player mode, cannot restore`);
      return;
    }
    
    const currentData = currentSegment.data as FollowPlayerData;
    const spline = currentData.spline;
    
    // Get player position
    const player = services.entities.getPlayer();
    if (!player?.mesh) {
      logger.triggers.error(`❌ camera_offset_change: Cannot get player mesh for restoration`);
      return;
    }
    
    const playerPos = player.mesh.getAbsolutePosition();
    const currentT = findTForPosition(spline, playerPos);
    
    // Restore previous camera offset
    const restoredCameraData: FollowPlayerData = {
      offset: storedState.previousOffset,
      lookAtOffset: storedState.previousLookAtOffset,
      smoothing: storedState.previousSmoothing,
      spline: spline
    };
    
    const transitionDuration = (ctx.params.transitionDuration as number) ?? 1.0;
    
    services.camera.setCameraMode(
      currentSegment.type,
      restoredCameraData,
      currentT,
      transitionDuration
    );
    
    logger.triggers.log(
      `📷 camera_offset_change: Restored previous camera offset: (${storedState.previousOffset.x.toFixed(2)}, ` +
      `${storedState.previousOffset.y.toFixed(2)}, ${storedState.previousOffset.z.toFixed(2)})`
    );
    
    // Clean up stored state
    delete ctx.self.metadata!.cameraState;
  }
};
